import React, { KeyboardEvent, createContext, useContext } from 'react'
import { LOGGER, Memoize, ParseKey } from '@cicero/data/utils'
import { BehaviorSubject, Subject } from 'rxjs'
import { DefaultElement, DefaultLeaf, Editable, ReactEditor, RenderElementProps, Slate, useFocused, useSelected, withReact } from 'slate-react'
import { Editor, Element, Node, Path, Range, createEditor } from 'slate'
import { IDocumentContent, PageDoc, Store } from '../models'
import { WidgetDefaultRenderer } from './elements'
import { ContentCommands } from './commands'

export class EditorService {
  static createEditor = () => withReact(createEditor())
  static useEditor = () => useContext(EditorContext)
  static useSelected = useSelected
  static useFocused = useFocused

  public elements = new Map<string, IElementDefinition>()
  public leaves = new Map<string, LeafRenderer>()
  public widgets = new Map<string, IEditorWidget>()
  public commands: IEditorCommand[] = []
  public shortcuts: { [key: string]: IEditorCommand[] } = {}
  public state = {
    currentFragment$: new Subject<any>(),
    currentFragmentText$: new Subject<string>(),
    currentPhrase$: new Subject<string|null>(),
    currentSelection$: new Subject<Range|null>(), // The current selection, if any
    currentWord$: new Subject<string|null>(),
    latestPoint$: new BehaviorSubject<Path>([]),
    latestSelection$: new BehaviorSubject<Range|null>(null), // The most recent selection, even if you clicked outside the editor
  }

  constructor(public editor: ReactEditor, public plugins: IEditorPlugin[] = [], public page: PageDoc, public meta?: any) {
    this.plugins.forEach((p) => this.registerPlugin(p))
    LOGGER.debug('[INITIALIZING EDITOR]', { editor, service: this, ...meta })
  }

  registerPlugin(plugin: IEditorPlugin) {
    let commands: IEditorCommand[] = plugin.commands || [];
    (plugin.widgets || []).forEach(widget => this.widgets.set(widget.id, widget))
    Object.entries(plugin.elements || {}).forEach(([key, handler]) => {
      this.elements.set(key, handler)
      if (typeof handler !== 'function') {
        if (handler.setup) { handler.setup(this) }
        if (handler.commands) {
          handler.commands = handler.commands.map(c => ({ elements: [key], ...c }))
          commands = commands.concat(handler.commands)
        }
      }
    })
    Object.entries(plugin.leaves || {}).forEach(([key, handler]) => this.leaves.set(key, handler))

    this.commands = this.commands.concat(commands)
    if (commands.length) {
      commands.forEach(cmd => {
        if (cmd.shortcut) {
          if (this.shortcuts[cmd.shortcut] === undefined) { this.shortcuts[cmd.shortcut] = [] }
          this.shortcuts[cmd.shortcut].push(cmd)
        }
      })
    }

    if (plugin.setup) { this.editor = plugin.setup(this.editor, this) }
  }

  renderElement(props: any) {
    const { type } = props.element
    const handlerIfExists = this.elements.get(type)
    return handlerIfExists ? handlerIfExists.renderer(props) : <DefaultElement {...props}/>
  }

  renderLeaf(props: any) {
    const { leaf } = props
    const rendererIfExists = this.leaves.get(leaf.type || 'default')
    return rendererIfExists ? rendererIfExists(props) : <DefaultLeaf {...props}/>
  }

  renderWidget(leafNode: IDashboardWidgetElement) {
    const { widgetType } = leafNode
    const widget = this.widgets.get(widgetType)
    if (!widget) { return <p>Unknown Widget Type &quot;{widgetType}&quot;.</p> }
    if (!widget.renderer) { return <WidgetDefaultRenderer type={widgetType} leaf={leafNode} /> }
    return widget.renderer(leafNode)
  }

  /**
   * Before loading raw content attached to a page model, make any required changes
   * @param content 
   * @param store 
   */
  async preRender(content: IDocumentContent[], store: Store) {
    // LOGGER.debug('prepping', content, store)
    return Promise.all(content.map(async b => {
      const el = this.elements.get(b.type || '')
      if (el && typeof el !== 'function' && el.preRender) { return el.preRender(b, this, store) }
      return b
    }))
  }

  /**
   * Before saving content back to the page model, make any required changes
   * @param content 
   * @param store 
   */
  async preSave(content: IDocumentContent[], store: Store) {
    // LOGGER.debug('parsing', content, store)
    return Promise.all(content.map(async b => {
      const el = this.elements.get(b.type || '')
      if (el && typeof el !== 'function' && el.preSave) { return el.preSave(b, this, store) }
      return b
    }))
  }

  handleChange(content: IDocumentContent[]) {
    const { selection } = this.editor
    const state: any = { ...this.state }
    if (selection) {
      this.state.latestPoint$.next(selection?.focus?.path)
      this.state.latestSelection$.next(selection)
      this.state.currentSelection$.next(selection)

      if (Range.isCollapsed(selection)) {
        const [start] = Range.edges(selection)

        const fragment = Editor.fragment(this.editor, start)
        const fragmentText = Editor.string(this.editor, start.path)
        const wordBefore = Editor.before(this.editor, start, { unit: 'word' })
        const wordStart = wordBefore && Editor.before(this.editor, wordBefore)
        const beforeRange = wordStart && Editor.range(this.editor, wordStart, start)
        const beforeText = beforeRange && Editor.string(this.editor, beforeRange)
        const lineBefore = Editor.before(this.editor, start, { unit: 'line' })
        const lineStart = lineBefore && Editor.before(this.editor, lineBefore)
        const lineBeforeRange = lineStart && Editor.range(this.editor, lineStart, start)
        const lineBeforeText = lineBeforeRange && Editor.string(this.editor, lineBeforeRange)
        const textSinceLastSpecial = fragmentText?.match(/(\/|#|@)([A-z0-9\s.]*)$/gm)

        state.beforeText = beforeText
        state.lineBeforeText = lineBeforeText
        state.textSinceLastSpecial = textSinceLastSpecial ? textSinceLastSpecial[0] : null

        this.state.currentFragment$.next(fragment)
        this.state.currentFragmentText$.next(fragmentText)
        this.state.currentPhrase$.next(state.textSinceLastSpecial)
      }

    } else {
      this.state.currentSelection$.next(null)
    }

    this.plugins.forEach(p => {
      if (p.handleChange) { p.handleChange(content, this) }
    })

    LOGGER.info('[CONTENT_CHANGE]', { content, service: this, editor: this.editor, state })
  }

  handleKeyPress(event: KeyboardEvent) {
    const keycode = ParseKey(event)
    const commands = this.shortcuts[keycode]
    if (commands) { // If it's a known shortcut
      for (const command of commands) {
        let shouldExecute = true
        if (command.elements) { // Check the selected element(s) matches an allowed one for this command
          shouldExecute = ContentCommands.isBlockType(this.editor, command.elements)
        }

        if (shouldExecute) { // Matches the first valid command for this element type and returns
          LOGGER.debug('[CMD]', command.title, command)
          command.handler(this.editor, this)
          event.preventDefault()
          return
        }
      }
    }

    // Otherwise handle generically
    this.plugins.forEach(p => {
      if (p.handleKeyPress) { p.handleKeyPress(event, this) }
    })
  }

  locate(el: Node): Path {
    const { children } = this.editor
    return [children.indexOf(el)]
  }

  @Memoize(q => q ? q.search : '')
  renderCommandList(q: { search?: string } = {}) {
    if (Object.keys(q).length === 0) { return this.commands }
    const { search } = q
    let commands = this.commands
    if (search) {
      commands = this.commands.filter(c => {
        const term = search.toLowerCase()
        const searchSpace = c.title.toLowerCase()
        return searchSpace.indexOf(term) > -1
      })
    }
    return commands
  }

  destroy() {
    LOGGER.debug('destroying editor', this)
  }
}

export const EditorContext = createContext<EditorService>(undefined as any)
export const SlateEditor = Slate
export const SlateEditable = Editable

export type ElementRenderer = (props: any) => JSX.Element
export type LeafRenderer = (props: any) => JSX.Element

export interface IElementDefinition {
  commands?: IEditorCommand[]
  preRender?(el: IDocumentContent, service: EditorService, store: Store): Promise<IDocumentContent>
  preSave?(el: IDocumentContent, service: EditorService, store: Store): Promise<IDocumentContent>
  renderer: ElementRenderer
  setup?(service: EditorService): void
}

export interface IEditorPlugin {
  setup?(editor: ReactEditor, service: EditorService): ReactEditor
  handleKeyPress?(ev: KeyboardEvent, service: EditorService): void
  handleChange?(content: IDocumentContent[], service: EditorService): void
  elements?: { [type: string]: IElementDefinition }
  leaves?: { [type: string]: LeafRenderer }
  widgets?: IEditorWidget[]
  commands?: IEditorCommand[]
}

export interface IEditorElementProps<El> {
  children: any
  attributes: any
  element: El
}

export interface IEditorLeafProps<Leaf> {
  children: any
  attributes: any
  leaf: Leaf
  text: Leaf
}

export interface IEditorCommand {
  title: string
  hidden?: boolean
  shortcut?: string
  elements?: string[]
  handler(editor: ReactEditor, service: EditorService): void
}

export interface IEditorWidgetConfigSchema<T> {
  key: string
  label: string
  renderForm(value: T, onChange: (value: T) => void): JSX.Element
  required?: boolean
  defaultValue?: T
  validate(value: string): string|null
}

export interface IEditorWidget {
  id: string
  name: string
  description: string
  image: string
  renderer?(leaf: IDashboardWidgetElement): JSX.Element
  formRenderer?(leaf: IDashboardWidgetElement, onChange: (leaf: IDashboardWidgetElement) => any): JSX.Element
  configSchema?: IEditorWidgetConfigSchema<any>[]
}

export interface IDashboardWidgetElement extends Element {
  [key: string]: any
  type: 'widget'
  widgetType: string
  title: string
  size?: number
  stick?: 'left'|'right'|'fill'
  // config: T
}

export interface IDashboardElement extends Element {
  type: 'dashboard'
  children: IDashboardWidgetElement[]
}

export interface IRenderElementProps<T extends Element> extends RenderElementProps {
  element: T
}
